// components/payments/MercadoPagoPaymentSheet.tsx
import {
  ActionIcon,
  Badge,
  Button,
  Code,
  Divider,
  Drawer,
  Flex,
  Group,
  Loader,
  Stack,
  Text,
  ThemeIcon,
  Title,
  Tooltip,
  CopyButton,
  Grid,
  Select,
  NumberInput
} from '@mantine/core';
import {
  IconCheck,
  IconX,
  IconAlertTriangle,
  IconCircleDashed,
  IconCopy,
  IconRefresh,
  IconCurrencyDollar
} from '@tabler/icons-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMpAttemptChannel, type MpAttemptUpdate } from '../hooks/useMpAttemptChannel';
import {
  useCancelMPAttempt,
  useCreateMPAttempt,
  useGetMPAttemptStatus
} from '../hooks/integration/mutations';
import { showNotification } from '../utils/Notification/Notification';
import { useGetCashierboxes } from '../hooks/cashierboxes/queries';
import { useForm } from '@mantine/form';
import { useTranslation } from 'react-i18next';
import { requiredFieldFormValidator } from '../utils/validators/formValidators';
import { useGetStoredMPTerminals } from '../hooks/integration/queries';
import { usePostHog } from 'posthog-js/react';
import { trackEvent } from '../utils/monitoring/posthog';
import { MonitoringEventTypes } from '../utils/monitoring/Monitoring_Events_Enum';
import { buildOrderFolio } from '../utils/ordersUtils';
import { formatMoneyString } from '../utils/strings';
import { resolveCustomerDisplayAttemptState, type CustomerDisplayPaymentOverride } from '@washday/utilities';

// --- utils/local ---
const statusBadgeColor: Record<MpAttemptUpdate['status'], string> = {
  pending: 'blue',
  approved: 'green',
  rejected: 'red',
  cancelled: 'gray',
  refunded: 'violet',
  charged_back: 'orange'
};

function niceAmount(n?: number, currencySymbol?: string) {
  if (typeof n !== 'number') return '—';
  return formatMoneyString(n, 2, '.', ',', currencySymbol);
}

// --- tipos de props mínimos para no arrastrar todo el dominio ---
type MinimalOrder = {
  _id?: string;
  cashierBoxId?: string;
  sequence?: number | string;
  folioPrefix?: string;
  total?: number;
  amountPaid?: number;
  currency?: string;
  paymentLines?: Array<{ amountPaid?: number }>;
  mpAttempt?: {
    terminalId?: string; // WASHDAY INTERNAL _ID of the payment terminal
    attemptId?: string;
    status?: string;
    external_reference?: string;
    terminal?: { id?: string; label?: string };
    mp_order_id?: string;
    mp_payment_id?: string;
    amount?: number;
  };
  createdIn?: { _id?: string; currencySymbol?: string }; // storeId
};

type Props = {
  onPaymentStateChange?: (status: CustomerDisplayPaymentOverride | 'confirmed', order?: MinimalOrder) => void;
  opened: boolean;
  onClose: () => void;
  attemptId?: string | null; // "seed"
  order?: MinimalOrder | null;
  storeId?: string;
  // Opcionales para integraciones externas de UI
  onRetryPayment?: (newAttemptId?: string) => Promise<void> | void;
  onCancelAttempt?: () => Promise<void> | void;
  onPrintTicket?: (order: any) => Promise<void> | void;
  onApproved?: (approvedOrder: any) => Promise<void> | void;
  autoCloseOnApproved?: boolean;
  // (opcional) permitir cambiar terminal desde el form previo:
  allowTerminalSelect?: boolean;
  print_on_terminal?: 'no_ticket' | 'seller_ticket';
};

export function MercadoPagoPaymentSheet({
  onPaymentStateChange,
  opened,
  onClose,
  attemptId,
  order,
  storeId,
  onRetryPayment,
  onCancelAttempt,
  onPrintTicket,
  onApproved,
  autoCloseOnApproved = false,
  allowTerminalSelect = false,
  print_on_terminal,
}: Props) {
  const posthog = usePostHog();
  const { t } = useTranslation(['FieldName', 'MercadoPagoSheet']);
  const [currentAttemptId, setCurrentAttemptId] = useState<string | null>(attemptId ?? null);
  const [retryData, setRetryData] = useState<{
    terminalId?: string;
    cashierBoxId?: string;
    amount?: number;
  }>({
    terminalId: order?.mpAttempt?.terminal?.id ?? undefined,
    cashierBoxId: order?.cashierBoxId ?? undefined,
    amount: order?.mpAttempt?.amount ?? undefined
  }); // in case we are opening a sheet with already an attempt
  const [creating, setCreating] = useState(false);
  const { data: storedMPTerminals } = useGetStoredMPTerminals({ storeId: storeId || '' }, !!storeId && allowTerminalSelect);

  // seed desde prop
  useEffect(() => {
    // si el padre cambia el attemptId (p.ej. al abrir el sheet), lo tomamos como seed
    setCurrentAttemptId(attemptId ?? null);
  }, [attemptId]);

  useEffect(() => {
    hasNotifiedApproved.current = false;
  }, [currentAttemptId]);

  // Cajas
  const { isLoading: isCashierboxesLoading, data: cashierboxesData } = useGetCashierboxes(
    !!storeId,
    storeId || ''
  );
  const cashierboxes = cashierboxesData || [];

  // Mutations
  const { mutate: createMPAttempt } = useCreateMPAttempt();
  const { mutate: cancelMPAttempt, isPending: isCancelling } = useCancelMPAttempt();
  const { mutate: getMPAttemptStatus, isPending: isGettingStatus } = useGetMPAttemptStatus();
  // guard to avoid double-calling onApproved (auto-close effect + general effect)
  const hasNotifiedApproved = useRef(false);

  // Listener en vivo (UN solo canal)
  const enabled = Boolean(currentAttemptId);
  const channel = useMpAttemptChannel({
    attemptId: (currentAttemptId as string) || '',
    enabled
  });

  const {
    online,
    last,
    isPending,
    isApproved,
    isRejected,
    isCancelled,
    isRefunded,
    isChargedBack
  } = channel;
  const hasAttempt = Boolean(currentAttemptId);
  const isTerminalStatus = isApproved || isCancelled || isRejected || isRefunded || isChargedBack;
  const shouldConfirmClose = hasAttempt && !isTerminalStatus;
  const displayStateCallback = useRef(onPaymentStateChange);
  displayStateCallback.current = onPaymentStateChange;
  useEffect(() => {
    if (!hasAttempt && !creating) return;
    const status = creating ? 'processing' : isApproved ? 'confirmed' : isCancelled ? 'cancelled' : isRejected || isRefunded || isChargedBack ? 'failed' : 'pending';
    displayStateCallback.current?.(status, order ? { ...order, paymentLines: last?.order?.paymentLines ?? order.paymentLines } : undefined);
  }, [opened, hasAttempt, creating, isApproved, isCancelled, isRejected, isRefunded, isChargedBack, currentAttemptId, last?.order?.paymentLines]);

  // Texto de estado
  const label = useMemo(() => {
    if (!enabled && !creating) return t('MercadoPagoSheet:info.attempt.status.message.pending');
    if (creating) return t('MercadoPagoSheet:info.attempt.status.message.creating');
    const os = (last?.orderStatus || '').toLowerCase();
    const ps = (last?.paymentStatus || '').toLowerCase();
    if (isApproved) return t('MercadoPagoSheet:info.attempt.status.message.approved');
    if (isRefunded) return t('MercadoPagoSheet:info.attempt.status.message.refunded');
    if (isChargedBack) return t('MercadoPagoSheet:info.attempt.status.message.charged_back');
    if (isCancelled) return t('MercadoPagoSheet:info.attempt.status.message.cancelled');
    if (isRejected) return t('MercadoPagoSheet:info.attempt.status.message.rejected');
    if (os === 'at_terminal' || ps === 'in_process') return t('MercadoPagoSheet:info.attempt.status.message.processing');
    if (os === 'created') return t('MercadoPagoSheet:info.attempt.status.message.sentToTerminal');
    return t('MercadoPagoSheet:info.attempt.status.message.waiting');
  }, [
    enabled,
    creating,
    isApproved,
    isRefunded,
    isCancelled,
    isRejected,
    isChargedBack,
    last?.orderStatus,
    last?.paymentStatus,
    t
  ]);

  // --- Form para la rama fallback (sin attempt) ---
  const amountPaid = (order?.paymentLines || []).reduce(
    (acc, pl) => acc + (pl?.amountPaid || 0),
    0
  );
  const defaultAmount = Math.max(0, (order?.total || 0) - amountPaid);

  const form = useForm<{
    cashierBoxId: string;
    amount: number | string;
    terminalId?: string;
  }>({
    mode: 'uncontrolled',
    initialValues: {
      cashierBoxId: order?.cashierBoxId || (cashierboxes[0]?._id as string) || '',
      amount: defaultAmount || '',
      terminalId: order?.mpAttempt?.terminal?.id || storedMPTerminals?.[0]?._id || ''
    },
    validate: {
      cashierBoxId: (value) => {
        const requiredValidatorRes = requiredFieldFormValidator(value);
        if (requiredValidatorRes) return requiredValidatorRes;
      },
      amount: (value) => {
        const requiredValidatorRes = requiredFieldFormValidator(value);
        if (requiredValidatorRes) return requiredValidatorRes;
        const num = Number(value);
        if (!value && value !== 0) return requiredValidatorRes;
        if (Number.isNaN(num)) return 'Monto inválido';
        if (num <= 0) return 'El monto debe ser mayor a 0';
        if (defaultAmount && num > defaultAmount) return 'El monto debe ser menor o igual al monto total pendiente';
        return null;
      }
    }
  });

  // Crear intento (desde el form o desde "Reintentar")
  const doCreateAttempt = (payload: {
    orderId?: string;
    amount: number;
    currency?: string;
    storeId?: string;
    terminalId?: string;
    cashierBoxId: string;
  }) => {
    if (!payload?.orderId && !storeId) {
      showNotification({
        type: 'Error',
        title: 'Faltan datos',
        message: 'Se requiere orderId o storeId para crear el intento.'
      });
      return;
    }
    setCreating(true);
    createMPAttempt(
      {
        orderId: payload.orderId!,
        amount: payload.amount || retryData.amount || 0,
        currency: payload?.currency || order?.currency || 'MXN',
        storeId: payload?.storeId || order?.createdIn?._id || storeId!,
        terminalId: payload?.terminalId || retryData.terminalId || '',
        cashierBoxId: payload.cashierBoxId || retryData.cashierBoxId || '',
        print_on_terminal: print_on_terminal
      },
      {
        onSuccess: (data: any) => {
          const newId =
            data?.attemptId || data?._id || data?.attempt?.attemptId || data?.attempt?._id || null;
          const attemptTerminalId = data?.terminal?.id;
          setRetryData({
            terminalId: attemptTerminalId,
            cashierBoxId: payload.cashierBoxId,
            amount: data?.amount || payload.amount
          });
          if (!newId) {
            displayStateCallback.current?.('failed', order ?? undefined);
            showNotification({
              type: 'Error',
              title: 'No se obtuvo attemptId',
              message: 'El backend no devolvió un identificador de intento.'
            });
            setCreating(false);
            return;
          }
          setCurrentAttemptId(String(newId));
          setCreating(false);
          showNotification({
            type: 'Success',
            title: 'Intento creado',
            message: `Se creó el intento ${String(newId).slice(-6)}`
          });
          trackEvent(posthog, MonitoringEventTypes.MERCADO_PAGO_ATTEMPT_CREATED_SUCCESS, {});
          // si te interesa notificar al padre:
          onRetryPayment?.(String(newId));
        },
        onError: (error: any) => {
          setCreating(false);
          displayStateCallback.current?.('failed', order ?? undefined);
          const message =
            error?.response?.data?.message || error?.message || 'Error al crear el intento.';
          showNotification({
            autoClose: false,
            type: 'Error',
            title: 'No se pudo crear el intento',
            message
          });
          trackEvent(posthog, MonitoringEventTypes.MERCADO_PAGO_ATTEMPT_CREATED_FAIL, {
            error: message
          });
        }
      }
    );
  };

  const handleManualRefresh = () => {
    if (!currentAttemptId) return;
    getMPAttemptStatus(
      { attemptId: currentAttemptId },
      {
        onSuccess: (r) => {
          showNotification({
            type: 'Info',
            title: t('MercadoPagoSheet:info.attemptRefreshed.title'),
            message: t('MercadoPagoSheet:info.attemptRefreshed.msg', { id: String(r.attemptId).slice(-6), status: t(`MercadoPagoSheet:status.${r.status}`) })
          });
        },
        onError: (e: any) => {
          showNotification({
            type: 'Error',
            title: 'No se pudo actualizar',
            message: e?.message || 'Error inesperado'
          });
        }
      }
    );
  };

  const handleCancelAttempt = () => {
    if (!currentAttemptId) return;
    cancelMPAttempt(
      { attemptId: currentAttemptId },
      {
        onSuccess: (res: any) => {
          showNotification({
            type: 'Info',
            title: t('MercadoPagoSheet:info.attemptCancelled.title'),
            message: t('MercadoPagoSheet:info.attemptCancelled.msg', { status: res?.status || 'cancelled' })
          });
          onCancelAttempt?.();
          const displayState = resolveCustomerDisplayAttemptState(res?.status);
          displayStateCallback.current?.(displayState, order ?? undefined);
          if (!onPaymentStateChange || (displayState !== 'pending' && displayState !== 'processing')) setCurrentAttemptId(null);
        },
        onError: (error: any) => {
          const message =
            error?.response?.data?.message ||
            error?.message ||
            'Error al cancelar intento de cobro.';
          showNotification({
            autoClose: false,
            type: 'Error',
            title: 'Error al cancelar',
            message
          });
        }
      }
    );
  };

  const handleRetry = () => {
    if (!order?._id) {
      showNotification({
        type: 'Error',
        title: 'Falta orderId',
        message: 'No se puede reintentar sin un pedido.'
      });
      return;
    }
    trackEvent(posthog, MonitoringEventTypes.MERCADO_PAGO_RETRY_ATTEMPT, {});
    doCreateAttempt({
      orderId: order._id,
      amount: retryData.amount || Number(order.total || 0) - Number(order.amountPaid || 0),
      currency: order.currency || 'MXN',
      storeId: order.createdIn?._id || storeId,
      terminalId: retryData.terminalId || undefined,
      cashierBoxId: retryData.cashierBoxId || order.cashierBoxId || (cashierboxes[0]?._id as string)
    });
  };

  const handlePrintTicket = () => {
    if (!order) return;
    onPrintTicket?.({ ...order, paymentLines: last?.order?.paymentLines });
  };

  const handleClose = () => {
    if (shouldConfirmClose) {
      const confirmed =
        window.confirm(
          t('MercadoPagoSheet:confirmClose.pending') ||
            'El intento sigue en proceso. ¿Cerrar de todos modos? Puede asentarse más tarde; revisa terminal/webhooks.'
        ) || false;
      if (!confirmed) return;
    }
    onClose?.();
    if (!onPaymentStateChange || isTerminalStatus) setCurrentAttemptId(null);
    setRetryData({
      terminalId: undefined,
      cashierBoxId: undefined,
      amount: undefined
    });
    hasNotifiedApproved.current = false;
  };

  // Auto-close si está aprobado
  useEffect(() => {
    if (autoCloseOnApproved && isApproved) {
      const t = setTimeout(onClose, 900);
      hasNotifiedApproved.current = true;
      onApproved?.({ ...order, paymentLines: last?.order?.paymentLines });
      return () => clearTimeout(t);
    }
  }, [autoCloseOnApproved, isApproved, onClose]);

  useEffect(() => {
    if (isApproved && !hasNotifiedApproved.current) {
      hasNotifiedApproved.current = true;
      onApproved?.({
        ...order,
        paymentLines: last?.order?.paymentLines,
        attemptId:
          (last as any)?.attemptId ||
          currentAttemptId ||
          (last as any)?.mp_payment_id ||
          order?.mpAttempt?.attemptId
      });
    }
  }, [isApproved, onApproved, order, last?.order?.paymentLines, last, currentAttemptId]);

  useEffect(() => {
    if (!opened) {
      hasNotifiedApproved.current = false;
    }
  }, [opened]);

  useEffect(() => {
    form.setFieldValue('amount', defaultAmount?.toString() || '');
  }, [defaultAmount]);

  useEffect(() => {
    if (opened) {
      trackEvent(posthog, MonitoringEventTypes.MERCADO_PAGO_SHEET_VIEWED, {
        orderId: order?._id,
        amount: defaultAmount,
        currency: order?.currency,
        storeId: storeId,
      });
    }
  }, [opened]);

  // <<< OPCIONAL/REDUNDANTE: invalidación en un efecto (además del onEvent) >>>
  // useEffect(() => {
  //   if (last?.order?.id && last?.order?.shouldRefetch) {
  //     // >>>>>>>>>>>>>>>>>> AQUÍ invalidas la query de la orden <<<<<<<<<<<<<<<<<
  //     // Ejemplo estándar TanStack:
  //     // queryClient.invalidateQueries({ queryKey: ['order', last.order._id] });
  //     queryClient.invalidateQueries({ queryKey: ['order', last.order._id] });
  //   }
  // }, [last?.order?.id, last?.order?.shouldRefetch, queryClient]);

  // --- Render ---
  const statusNow = (last?.status as MpAttemptUpdate['status']) || 'pending';
  const StatusIcon = isApproved
    ? IconCheck
    : isRejected || isCancelled
      ? IconX
      : isRefunded
        ? IconCheck
        : isChargedBack
          ? IconAlertTriangle
          : IconCircleDashed;

  const amountToCharge = useMemo(() => {
    if (hasAttempt) {
      const orderAttemptAmount = order?.mpAttempt?.amount;
      const lastAttemptAmount = last?.amount;
      const attemptAmount = lastAttemptAmount || orderAttemptAmount || 0;
      return niceAmount(attemptAmount, order?.createdIn?.currencySymbol);
    }
    return "--";
  }, [hasAttempt, order?.createdIn?.currencySymbol, last?.amount]);

  const orderFolio = buildOrderFolio(order?.folioPrefix, order?.sequence);
  return (
    <Drawer
      opened={opened}
      onClose={handleClose}
      position="right"
      closeOnClickOutside={false}
      size="lg"
      overlayProps={{ opacity: 0.3, blur: 2 }}
      withCloseButton
      title={
        <Group gap="sm">
          <Title order={4}>{t('MercadoPagoSheet:title')}</Title>
          {hasAttempt && (
            <Badge color={statusBadgeColor[statusNow]} size="sm" variant="filled">
              {t(`MercadoPagoSheet:status.${statusNow}`)}
            </Badge>
          )}
        </Group>
      }>
      <Stack gap="md">
        {/* Encabezado y monto */}
        <Group justify="space-between" align="center">
          <Stack gap={2}>
            <Text size="sm" c="dimmed">
              {t('MercadoPagoSheet:orderLabel')}
            </Text>
            <Group gap="xs">
              <Code>{orderFolio ?? order?._id?.slice(-6) ?? '—'}</Code>
            </Group>
          </Stack>
          <Stack gap={2} align="flex-end">
            <Text size="sm" c="dimmed">
              {t('MercadoPagoSheet:amountToCharge')}
            </Text>
            <Title order={3}>{amountToCharge}</Title>
          </Stack>
        </Group>

        <Divider />

        {/* Si NO hay attempt -> mostrar form previo */}
        {!hasAttempt ? (
          <Stack gap="md">
            <CreateAttemptView
              t={t}
              form={form}
              cashierboxes={cashierboxes?.map((cb) => ({ _id: cb._id || '', name: cb.name })) || []}
              isCashierboxesLoading={isCashierboxesLoading}
              allowTerminalSelect={allowTerminalSelect}
              availableTerminals={storedMPTerminals?.map((terminal) => ({ id: terminal.mp?.mp_terminal_id || '', label: terminal.label || '' })) || []}
            />

            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                {t('MercadoPagoSheet:prefill.help')}
              </Text>
              <Group>
                <Button variant="outline" onClick={handleClose}>
                  {t('MercadoPagoSheet:close')}
                </Button>
                <Button
                  onClick={() => {
                    const ok = form.validate();
                    if (ok.hasErrors) return;
                    const values = form.getValues();
                    const selectedCashierBox = cashierboxes.find((cb) => cb._id === values.cashierBoxId);
                    const cashierBoxPreferredTerminalId = selectedCashierBox?.mpConfig?.preferredTerminalId?.toString();
                    doCreateAttempt({
                      orderId: order?._id || '',
                      amount: Number(values.amount),
                      currency: order?.currency || 'MXN',
                      storeId: storeId || order?.createdIn?._id || '',
                      terminalId: values.terminalId || cashierBoxPreferredTerminalId || undefined, // if no terminalId is selected, use the preferred terminalId of the cashier box
                      cashierBoxId: values.cashierBoxId
                    });
                  }}
                  loading={creating}
                >
                  {t('MercadoPagoSheet:createAttempt')}
                </Button>
              </Group>
            </Group>
          </Stack>
        ) : (
          <>
            {/* Estado en vivo */}
            <Flex direction="column" align="center" gap="sm" py="sm">
              {isPending ? (
                <Loader size="lg" />
              ) : (
                <ThemeIcon size={48} radius="xl" color={statusBadgeColor[statusNow]}>
                  <StatusIcon size={28} />
                </ThemeIcon>
              )}
              <Text ta="center">{label}</Text>
              <Text size="xs" c="dimmed">
                {t('MercadoPagoSheet:realtime.label')}: {online ? t('MercadoPagoSheet:realtime.connected') : t('MercadoPagoSheet:realtime.offline')}{' '}
                {last?.ts ? `• ${new Date(last.ts).toLocaleTimeString()}` : ''}
              </Text>
            </Flex>

            {/* Detalles técnicos */}
            <Stack gap={6}>
              <Group gap="xs" wrap="wrap">
                <Text size="sm" fw={500}>
                  {t('MercadoPagoSheet:tech.attempt')}:
                </Text>
                <Code>{currentAttemptId ?? '—'}</Code>
                {currentAttemptId && (
                  <CopyField value={currentAttemptId} ariaLabel="Copiar attemptId" t={t} />
                )}
              </Group>

              <Group gap="xs" wrap="wrap">
                <Text size="sm" fw={500}>
                  {t('MercadoPagoSheet:tech.orderId')}:
                </Text>
                <Code>{last?.mp_order_id ?? '—'}</Code>
                {last?.mp_order_id && (
                  <CopyField value={last.mp_order_id} ariaLabel="Copiar mp_order_id" t={t} />
                )}
              </Group>

              <Group gap="xs" wrap="wrap">
                <Text size="sm" fw={500}>
                  {t('MercadoPagoSheet:tech.paymentId')}:
                </Text>
                <Code>{last?.mp_payment_id ?? '—'}</Code>
                {last?.mp_payment_id && (
                  <CopyField value={last.mp_payment_id} ariaLabel="Copiar mp_payment_id" t={t} />
                )}
              </Group>
            </Stack>

            <Divider />

            {/* Acciones */}
            <Group justify="space-between">
              <Group>
                <Button
                  variant="light"
                  onClick={handlePrintTicket}
                  disabled={!isApproved || !order}
                >
                  {t('MercadoPagoSheet:printTickets')}
                </Button>
              </Group>

              <Group>
                <Button
                  leftSection={<IconRefresh size={16} />}
                  variant="outline"
                  onClick={handleManualRefresh}
                  disabled={!currentAttemptId || isGettingStatus}
                  loading={isGettingStatus}
                >
                  {t('MercadoPagoSheet:refreshStatus')}
                </Button>

                {(isRejected || isCancelled) && (
                  <Button color="blue" onClick={handleRetry} disabled={creating || isCancelling}>
                    {t('MercadoPagoSheet:retry')}
                  </Button>
                )}

                {isPending && (
                  <Button
                    color="gray"
                    variant="outline"
                    onClick={handleCancelAttempt}
                    disabled={!currentAttemptId || isCancelling}
                  >
                    {t('MercadoPagoSheet:cancelAttempt')}
                  </Button>
                )}

                <Button color="dark" variant="subtle" onClick={handleClose}>
                  {t('MercadoPagoSheet:close')}
                </Button>
              </Group>
            </Group>
          </>
        )}
      </Stack>
    </Drawer>
  );
}

// --- Sub-componente del formulario previo ---
function CreateAttemptView({
  t,
  form,
  cashierboxes,
  isCashierboxesLoading,
  allowTerminalSelect,
  availableTerminals = []
}: {
  t: any;
  form: ReturnType<typeof useForm<any>>;
  cashierboxes: Array<{ _id: string; name: string }>;
  isCashierboxesLoading: boolean;
  allowTerminalSelect: boolean;
  availableTerminals: Array<{ id: string; label?: string }>;
}) {
  return (
    <Grid>
      <Grid.Col span={12}>
        <Select
          label={t('FieldName:cashierBox', { ns: 'FieldName' })}
          placeholder={t('FieldName:cashierBox')}
          data={(cashierboxes || []).map((cb) => ({
            label: cb.name,
            value: cb._id
          }))}
          disabled={isCashierboxesLoading}
          allowDeselect={false}
          {...form.getInputProps('cashierBoxId')}
        />
      </Grid.Col>

      {allowTerminalSelect && (
        <Grid.Col span={12}>
          <Select
            label={t('FieldName:terminal')}
            placeholder={t('FieldName:terminal')}
            data={availableTerminals.map((t) => ({ label: t.label || t.id, value: t.id }))}
            allowDeselect={false}
            {...form.getInputProps('terminalId')}
          />
        </Grid.Col>
      )}

      <Grid.Col span={12}>
        <NumberInput
          label={t('FieldName:amount')}
          placeholder={t('FieldName:amount')}
          allowLeadingZeros={false}
          allowNegative={false}
          thousandSeparator
          decimalScale={2}
          leftSection={<IconCurrencyDollar size={16} />}
          {...form.getInputProps('amount')}
        />
      </Grid.Col>
    </Grid>
  );
}

// --- util de copiar ---
function CopyField({ value, ariaLabel, t }: { value: string; ariaLabel?: string; t: any }) {
  return (
    <CopyButton value={value} timeout={1200}>
      {({ copied, copy }) => (
        <Tooltip label={copied ? t('MercadoPagoSheet:copy.copied') : t('MercadoPagoSheet:copy.copy')} withArrow>
          <ActionIcon
            size="sm"
            variant={copied ? 'filled' : 'light'}
            color={copied ? 'teal' : 'gray'}
            aria-label={ariaLabel}
            onClick={copy}
          >
            <IconCopy size={14} />
          </ActionIcon>
        </Tooltip>
      )}
    </CopyButton>
  );
}
